import { z } from 'zod';
import { parseRecurrence } from './recurrence.js';
import { isPlainDate } from './dates.js';
/**
 * Схемы описаны один раз и используются формой на клиенте и обработчиком на
 * сервере. Сервер валидирует всегда, даже если клиент уже проверил
 * (docs/SECURITY.md, раздел 9).
 */
export const idSchema = z.uuid();
export const plainDateSchema = z
    .string()
    .refine(isPlainDate, { message: 'Ожидается локальная дата в формате YYYY-MM-DD' });
export const goalHorizons = ['quarter', 'year', 'longterm'];
export const goalProgressModes = ['tasks', 'metric', 'manual'];
export const goalStatuses = ['active', 'paused', 'achieved', 'dropped'];
export const projectStatuses = ['active', 'paused', 'done'];
export const taskStatuses = ['todo', 'done', 'dropped'];
export const taskPriorities = ['none', 'low', 'normal', 'high'];
const title = z.string().trim().min(1).max(200);
const note = z.string().trim().max(10_000);
const goalFields = z.object({
    title,
    note: note.optional(),
    horizon: z.enum(goalHorizons),
    targetDate: plainDateSchema.nullish(),
    progressMode: z.enum(goalProgressModes).default('tasks'),
    metricStart: z.number().finite().nullish(),
    metricTarget: z.number().finite().nullish(),
    metricCurrent: z.number().finite().nullish(),
    manualProgress: z.number().min(0).max(100).nullish(),
    color: z
        .string()
        .regex(/^#[0-9a-fA-F]{6}$/)
        .nullish(),
    emoji: z.string().max(8).nullish(),
});
const METRIC_REQUIRES_TARGET = {
    message: 'Для режима «ключевой результат» обязателен metricTarget',
    path: ['metricTarget'],
};
export const createGoalSchema = goalFields.refine((v) => v.progressMode !== 'metric' || v.metricTarget != null, METRIC_REQUIRES_TARGET);
/**
 * При смене режима на «ключевой результат» целевое значение должно прийти в
 * том же запросе: иначе цель на мгновение оказалась бы в режиме, который
 * невозможно посчитать.
 */
export const updateGoalSchema = goalFields
    .partial()
    .extend({ status: z.enum(goalStatuses).optional() })
    .refine((v) => v.progressMode !== 'metric' || v.metricTarget != null, METRIC_REQUIRES_TARGET);
export const createProjectSchema = z.object({
    title,
    note: note.optional(),
    goalId: idSchema.nullish(),
    status: z.enum(projectStatuses).default('active'),
    dueDate: plainDateSchema.nullish(),
});
export const createTaskSchema = z.object({
    title,
    note: note.optional(),
    projectId: idSchema.nullish(),
    scheduledFor: plainDateSchema.nullish(),
    dueDate: plainDateSchema.nullish(),
    estimateMinutes: z
        .number()
        .int()
        .min(0)
        .max(24 * 60)
        .nullish(),
    priority: z.enum(taskPriorities).default('none'),
    recurrenceRule: z
        .string()
        .max(500)
        .refine((v) => parseRecurrence(v) !== null)
        .nullish(),
    tags: z.array(z.string().trim().min(1).max(50)).max(30).optional(),
});
export const updateTaskSchema = createTaskSchema
    .partial()
    .extend({ status: z.enum(taskStatuses).optional(), sortOrder: z.number().int().optional() });
/** Быстрое добавление: строка целиком плюс возможность отключить разбор. */
export const quickAddSchema = z.object({
    projectId: idSchema.nullable().optional(),
    input: z.string().trim().min(1).max(500),
    parse: z.boolean().default(true),
});
/** Перенос набора задач на дату. Ограничение размера — защита от массовой операции. */
export const deferTasksSchema = z.object({
    ids: z.array(idSchema).min(1).max(500),
    scheduledFor: plainDateSchema,
});
export const listTasksQuerySchema = z.object({
    scope: z.enum(['today', 'inbox', 'all']).default('all'),
    date: plainDateSchema.optional(),
});
export const updateProjectSchema = createProjectSchema.partial();
export const archiveSchema = z.object({ archived: z.boolean() });
/** Краткая ссылка на проект и цель — чтобы связь была видна в карточке задачи. */
export const projectRefSchema = z.object({ id: idSchema, title: z.string() });
export const goalRefSchema = z.object({
    id: idSchema,
    title: z.string(),
    emoji: z.string().nullable(),
    color: z.string().nullable(),
});
/** Форма задачи, которую отдаёт API. */
export const taskSchema = z.object({
    id: idSchema,
    title: z.string(),
    note: z.string().nullable(),
    status: z.enum(taskStatuses),
    projectId: idSchema.nullable(),
    scheduledFor: plainDateSchema.nullable(),
    dueDate: plainDateSchema.nullable(),
    estimateMinutes: z.number().int().nullable(),
    priority: z.enum(taskPriorities),
    recurrenceRule: z.string().nullable(),
    completedAt: z.string().nullable(),
    sortOrder: z.number().int(),
    tags: z.array(z.string()),
    project: projectRefSchema.nullable(),
    goal: goalRefSchema.nullable(),
});
export const projectSchema = z.object({
    id: idSchema,
    title: z.string(),
    note: z.string().nullable(),
    goalId: idSchema.nullable(),
    status: z.enum(projectStatuses),
    dueDate: plainDateSchema.nullable(),
    sortOrder: z.number().int(),
    archivedAt: z.string().nullable(),
    taskCounts: z.object({ total: z.number().int(), done: z.number().int() }),
});
export const goalSchema = z.object({
    id: idSchema,
    title: z.string(),
    note: z.string().nullable(),
    horizon: z.enum(goalHorizons),
    targetDate: plainDateSchema.nullable(),
    progressMode: z.enum(goalProgressModes),
    metricStart: z.number().nullable(),
    metricTarget: z.number().nullable(),
    metricCurrent: z.number().nullable(),
    manualProgress: z.number().nullable(),
    status: z.enum(goalStatuses),
    color: z.string().nullable(),
    emoji: z.string().nullable(),
    createdAt: z.string(),
    archivedAt: z.string().nullable(),
    progressPercent: z.number().nullable(),
    taskCounts: z.object({ total: z.number().int(), done: z.number().int() }),
    pace: z.enum(['onTrack', 'behind', 'noData', 'noDeadline']),
    nextAction: z.object({ id: idSchema, title: z.string() }).nullable(),
});
export const goalSnapshotSchema = z.object({
    capturedAt: plainDateSchema,
    progressPercent: z.number(),
    note: z.string().nullable(),
});
/** Единый формат ошибки API. Внутренние подробности наружу не выходят. */
export const apiErrorSchema = z.object({
    error: z.object({
        code: z.string(),
        message: z.string(),
        requestId: z.string(),
    }),
});
export const bulkTasksSchema = z.object({
    ids: z.array(idSchema).min(1).max(500),
    patch: z
        .object({
        scheduledFor: plainDateSchema.nullable().optional(),
        projectId: idSchema.nullable().optional(),
    })
        .refine((v) => v.scheduledFor !== undefined || v.projectId !== undefined),
});
export const taskFilterSchema = z.object({
    priority: z.enum(taskPriorities).optional(),
    tag: z.string().trim().max(50).optional(),
    projectId: idSchema.optional(),
    status: z.enum(taskStatuses).optional(),
    routine: z.boolean().optional(),
});
export const savedFilterInputSchema = z.object({
    name: z.string().trim().min(1).max(100),
    filter: taskFilterSchema,
});
//# sourceMappingURL=schemas.js.map